import { Box, Button, Flex, Input, Text, chakra, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type { PaginationAction } from './types';

import { useI18n } from 'lib/i18n/context';
import Skeleton from 'ui/shared/chakra/Skeleton';

const ACTIVE_COLOR = '#14DCAC';
const CONTROL_RADIUS = '4px';

const PaginationArrow = ({ direction, color }: { direction: 'left' | 'right'; color: string }) => (
  <chakra.svg
    width="12px"
    height="12px"
    viewBox="0 0 12 12"
    fill="none"
    flexShrink={ 0 }
  >
    <path
      d={ direction === 'left' ? 'M7.5 2.5L4 6L7.5 9.5' : 'M4.5 2.5L8 6L4.5 9.5' }
      stroke={ color }
      strokeWidth="1.333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </chakra.svg>
);

interface Props {
  className?: string;
  currentPage: number;
  totalPages?: number | null;
  variant?: 'default' | 'explorer';
  label?: React.ReactNode;
  firstAction?: PaginationAction;
  prevAction?: PaginationAction;
  nextAction?: PaginationAction;
  lastAction?: PaginationAction;
  onPageSubmit?: (page: number) => void;
  isPageEditable?: boolean;
  isLoading?: boolean;
  isVisible?: boolean;
  showSkeleton?: boolean;
}

const getIndicatorWidth = (value: string) => `${ Math.max(value.length, 1) + 0.5 }ch`;

const PaginationControl = ({
  className,
  currentPage,
  totalPages,
  variant = 'default',
  firstAction,
  prevAction,
  nextAction,
  lastAction,
  onPageSubmit,
  isPageEditable = false,
  isLoading = false,
  isVisible = true,
  showSkeleton = false,
}: Props) => {
  const { t } = useI18n();
  const [ isEditing, setIsEditing ] = React.useState(false);
  const [ draftPage, setDraftPage ] = React.useState(String(currentPage));
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setDraftPage(String(currentPage));
  }, [ currentPage ]);

  React.useEffect(() => {
    if (!isEditing || !inputRef.current) {
      return;
    }

    inputRef.current.focus();
    inputRef.current.select();
  }, [ isEditing ]);

  // Cursor mode: API returns only next_page_params (no total_pages).
  // Degrade to First < N > (matches the retired CursorPagination look) — no total, no Last, no page jump.
  const isCursorMode = totalPages === null || totalPages === undefined;
  const isEditable = !isCursorMode && isPageEditable && Boolean(onPageSubmit) && !isLoading;
  const totalPagesText = totalPages ? String(totalPages) : '1';
  const isExplorer = variant === 'explorer';
  const controlHeight = isExplorer ? '24px' : '32px';
  const actionPaddingX = isExplorer ? '8px' : '20px';
  const arrowPadding = isExplorer ? '6px' : 0;
  const fontFamily = '\'Space Grotesk\', sans-serif';
  const mutedColor = useColorModeValue('#616161', '#797979');
  const borderColor = useColorModeValue('#E9ECEF', '#222222');
  const surfaceColor = useColorModeValue('#FFFFFF', '#111111');
  const hoverSurfaceColor = useColorModeValue('#FBFCFE', '#14DCAC');
  const hoverTextColor = useColorModeValue('#14DCAC', '#0A0A0A');
  const textColor = mutedColor;

  const handleEditStart = React.useCallback(() => {
    if (!isEditable) {
      return;
    }

    setDraftPage(String(currentPage));
    setIsEditing(true);
  }, [ currentPage, isEditable ]);

  const handleEditCancel = React.useCallback(() => {
    setDraftPage(String(currentPage));
    setIsEditing(false);
  }, [ currentPage ]);

  const handleEditSubmit = React.useCallback(() => {
    if (!onPageSubmit) {
      return;
    }

    const nextPage = Number(draftPage);

    if (!Number.isInteger(nextPage) || nextPage < 1) {
      handleEditCancel();
      return;
    }

    onPageSubmit(nextPage);
    setIsEditing(false);
  }, [ draftPage, handleEditCancel, onPageSubmit ]);

  const handleDraftChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setDraftPage(event.target.value.replace(/\D+/g, ''));
  }, []);

  const handleInputBlur = React.useCallback(() => {
    handleEditCancel();
  }, [ handleEditCancel ]);

  const handleInputKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleEditSubmit();
    }

    if (event.key === 'Escape') {
      handleEditCancel();
    }
  }, [ handleEditCancel, handleEditSubmit ]);

  const handleIndicatorClick = React.useCallback(() => {
    handleEditStart();
  }, [ handleEditStart ]);

  const renderAction = React.useCallback((action?: PaginationAction) => {
    if (!action) {
      return null;
    }

    const isDisabled = Boolean(action.isDisabled);

    if (action.direction) {
      return (
        <Button
          type="button"
          aria-label={ action.ariaLabel || t('shared.pagination.aria.action') }
          onClick={ action.onClick }
          isDisabled={ isDisabled }
          minW={ controlHeight }
          h={ controlHeight }
          p={ arrowPadding }
          borderRadius={ CONTROL_RADIUS }
          border="1px solid"
          borderColor={ borderColor }
          bg={ surfaceColor }
          color={ isDisabled ? mutedColor : ACTIVE_COLOR }
          _hover={ isDisabled ? {
            bg: surfaceColor,
            color: mutedColor,
          } : {
            bg: hoverSurfaceColor,
            color: hoverTextColor,
          } }
          _active={ isDisabled ? {
            bg: surfaceColor,
            color: mutedColor,
          } : {
            bg: hoverSurfaceColor,
            color: hoverTextColor,
          } }
          _disabled={{
            opacity: 1,
            cursor: 'not-allowed',
            color: mutedColor,
            bg: surfaceColor,
            borderColor,
          }}
        >
          <PaginationArrow direction={ action.direction } color="currentColor"/>
        </Button>
      );
    }

    return (
      <Button
        type="button"
        onClick={ action.onClick }
        isDisabled={ isDisabled }
        h={ controlHeight }
        minW="fit-content"
        px={ actionPaddingX }
        borderRadius={ CONTROL_RADIUS }
        border="1px solid"
        borderColor={ borderColor }
        bg={ surfaceColor }
        color={ isDisabled ? mutedColor : ACTIVE_COLOR }
        fontSize="12px"
        lineHeight="20px"
        fontWeight={ 500 }
        fontFamily={ fontFamily }
        _hover={ isDisabled ? {
          bg: surfaceColor,
          color: mutedColor,
        } : {
          bg: hoverSurfaceColor,
          color: hoverTextColor,
        } }
        _active={ isDisabled ? {
          bg: surfaceColor,
          color: mutedColor,
        } : {
          bg: hoverSurfaceColor,
          color: hoverTextColor,
        } }
        _disabled={{
          opacity: 1,
          cursor: 'not-allowed',
          color: mutedColor,
          bg: surfaceColor,
          borderColor,
        }}
      >
        { action.label }
      </Button>
    );
  }, [
    actionPaddingX,
    arrowPadding,
    borderColor,
    controlHeight,
    fontFamily,
    hoverSurfaceColor,
    hoverTextColor,
    mutedColor,
    surfaceColor,
    t,
  ]);

  if (!isVisible) {
    return null;
  }

  return (
    <Flex
      className={ className }
      alignItems="center"
      flexWrap="nowrap"
      columnGap="8px"
      rowGap={ 0 }
      fontSize="12px"
    >
      <Skeleton isLoaded={ !showSkeleton } borderRadius="base">
        { renderAction(firstAction) }
      </Skeleton>
      <Skeleton isLoaded={ !showSkeleton } borderRadius="base">
        { renderAction(prevAction) }
      </Skeleton>
      <Skeleton isLoaded={ !showSkeleton } borderRadius="base">
        { isCursorMode ? (
          <Box
            h={ controlHeight }
            px="10px"
            minW={ controlHeight }
            borderRadius={ CONTROL_RADIUS }
            border="1px solid"
            borderColor={ ACTIVE_COLOR }
            bg={ ACTIVE_COLOR }
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="12px"
            lineHeight="20px"
            fontWeight={ 700 }
            fontFamily={ fontFamily }
            color="#0A0A0A"
            userSelect="none"
          >
            { currentPage }
          </Box>
        ) : (
          <Box
            h={ controlHeight }
            pl="8px"
            pr="8px"
            width="fit-content"
            minW="fit-content"
            borderRadius={ CONTROL_RADIUS }
            border="1px solid"
            borderColor={ isEditing ? ACTIVE_COLOR : borderColor }
            bg={ surfaceColor }
            display="flex"
            alignItems="center"
            columnGap="4px"
            onClick={ isEditing ? undefined : handleIndicatorClick }
            cursor={ isEditable ? 'text' : 'default' }
            whiteSpace="nowrap"
          >
            { isEditing ? (
              <>
                <Text color={ textColor } fontSize="12px" lineHeight="20px" fontWeight={ 500 } fontFamily={ fontFamily }>
                  { t('shared.pagination.label.page') }
                </Text>
                <Input
                  ref={ inputRef }
                  value={ draftPage }
                  onChange={ handleDraftChange }
                  onBlur={ handleInputBlur }
                  onKeyDown={ handleInputKeyDown }
                  variant="unstyled"
                  inputMode="numeric"
                  autoComplete="off"
                  spellCheck={ false }
                  w={ getIndicatorWidth(draftPage) }
                  minW="1.5ch"
                  h="20px"
                  p={ 0 }
                  textAlign="center"
                  fontSize="12px"
                  lineHeight="20px"
                  fontWeight={ 500 }
                  color={ ACTIVE_COLOR }
                  fontFamily={ fontFamily }
                />
                <Text color={ textColor } fontSize="12px" lineHeight="20px" fontWeight={ 500 } fontFamily={ fontFamily }>
                  { t('shared.pagination.label.of') }
                </Text>
                <Text color={ textColor } fontSize="12px" lineHeight="20px" fontWeight={ 500 } fontFamily={ fontFamily }>{ totalPagesText }</Text>
              </>
            ) : (
              <>
                <Text color={ textColor } fontSize="12px" lineHeight="20px" fontWeight={ 500 } fontFamily={ fontFamily }>
                  { t('shared.pagination.label.page') }
                </Text>
                <Text color={ textColor } fontSize="12px" lineHeight="20px" fontWeight={ 500 } fontFamily={ fontFamily }>{ currentPage }</Text>
                <Text color={ textColor } fontSize="12px" lineHeight="20px" fontWeight={ 500 } fontFamily={ fontFamily }>
                  { t('shared.pagination.label.of') }
                </Text>
                <Text color={ textColor } fontSize="12px" lineHeight="20px" fontWeight={ 500 } fontFamily={ fontFamily }>{ totalPagesText }</Text>
              </>
            ) }
          </Box>
        ) }
      </Skeleton>
      <Skeleton isLoaded={ !showSkeleton } borderRadius="base">
        { renderAction(nextAction) }
      </Skeleton>
      { !isCursorMode && (
        <Skeleton isLoaded={ !showSkeleton } borderRadius="base">
          { renderAction(lastAction) }
        </Skeleton>
      ) }
    </Flex>
  );
};

export default React.memo(chakra(PaginationControl));
